import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { api, errMsg, todayIST } from "@/lib/api";

const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "—");
const dot = (bg, label = "") => L.divIcon({ className: "", html: `<div style="transform:translate(-11px,-11px);width:22px;height:22px;border-radius:50%;background:${bg};border:2px solid #fff;color:#fff;font:700 11px sans-serif;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.4)">${label}</div>` });

const Fit = ({ pts }) => {
  const map = useMap();
  useEffect(() => { if (pts.length) map.fitBounds(pts, { padding: [30, 30], maxZoom: 16 }); }, [pts.length]); // eslint-disable-line
  return null;
};

export const RouteHistory = ({ partners }) => {
  const [sid, setSid] = useState("");
  const [day, setDay] = useState(todayIST());
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  const [play, setPlay] = useState(false);
  const load = () => {
    if (!sid) return toast.error("Choose a partner.");
    api.get("/admin/route-history", { params: { staff_id: sid, day } }).then((r) => { setD(r.data); setI(Math.max(0, r.data.pings.length - 1)); setPlay(false); }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => {
    if (!play || !d) return;
    const id = setInterval(() => setI((x) => { if (x >= d.pings.length - 1) { setPlay(false); return x; } return x + 1; }), 300);
    return () => clearInterval(id);
  }, [play, d]);
  const pts = (d?.pings || []).map((p) => [p.lat, p.lng]);
  const cur = d?.pings[i];
  return (
    <div className="space-y-3 rounded-2xl border border-[#D2E0D8] bg-white p-4" data-testid="route-history">
      <p className="font-bold text-[#0D3B2B]">Route history (replay)</p>
      <div className="flex flex-wrap items-end gap-2">
        <select value={sid} onChange={(e) => setSid(e.target.value)} className="h-10 rounded-md border px-2 text-sm" data-testid="route-partner-select">
          <option value="">Choose partner</option>{partners.map((p) => <option key={p.staff_id} value={p.staff_id}>{p.name}</option>)}
        </select>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-10 rounded-md border px-2 text-sm" data-testid="route-date-input" />
        <button onClick={load} className="h-10 rounded-xl bg-[#0D3B2B] px-4 text-sm font-bold text-white" data-testid="route-load-btn">Show route</button>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" data-testid="route-stats">
            {[["Distance", `${d.km} km`, "km"], ["First seen", tm(d.first), "first"], ["Last seen", tm(d.last), "last"], ["Stops", d.stops.length, "stops"]].map(([l, v, k]) => (
              <div key={k} className="rounded-xl bg-[#F4F8F5] p-2"><p className="font-bold text-[#0D3B2B]" data-testid={`route-stat-${k}`}>{v}</p><p className="text-xs text-[#7B968B]">{l}</p></div>
            ))}
          </div>
          {pts.length ? (
            <>
              <div className="relative z-0 h-[380px] overflow-hidden rounded-xl">
                <MapContainer center={pts[0]} zoom={13} className="h-full w-full">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                  <Fit pts={pts} />
                  <Polyline positions={pts} pathOptions={{ color: "#9CA3AF", weight: 4 }} />
                  <Polyline positions={pts.slice(0, i + 1)} pathOptions={{ color: "#E05C3A", weight: 4 }} />
                  {d.stops.filter((s) => s.lat).map((s, k) => (
                    <Marker key={s.booking_id} position={[s.lat, s.lng]} icon={dot("#2E9E6B", k + 1)}>
                      <Popup><b>{s.booking_id}</b> · {s.status}<br />{s.address}<br />On the way {tm(s.on_way)} · Reached {tm(s.reached)} · Done {tm(s.completed_at)}</Popup>
                    </Marker>
                  ))}
                  {cur && <Marker position={[cur.lat, cur.lng]} icon={dot("#0D3B2B")} />}
                </MapContainer>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { if (i >= pts.length - 1) setI(0); setPlay(!play); }} className="h-9 rounded-lg bg-[#E05C3A] px-4 text-sm font-bold text-white" data-testid="route-play-btn">{play ? "Pause" : "Play"}</button>
                <input type="range" min={0} max={pts.length - 1} value={i} onChange={(e) => { setPlay(false); setI(Number(e.target.value)); }} className="flex-1" data-testid="route-slider" />
                <span className="w-20 text-right text-sm font-semibold" data-testid="route-time">{tm(cur?.at)}</span>
              </div>
            </>
          ) : <p className="rounded-xl bg-[#F4F8F5] p-4 text-sm text-[#7B968B]" data-testid="route-empty">No location recorded for this partner on this day.</p>}
          {d.stops.length > 0 && (
            <div className="space-y-1" data-testid="route-stops">
              {d.stops.map((s, k) => (
                <p key={s.booking_id} className="text-xs"><b>{k + 1}. {s.booking_id}</b> ({s.status}) · On the way {tm(s.on_way)} · Reached {tm(s.reached)} · Completed {tm(s.completed_at)}{s.total_kg ? ` · ${s.total_kg} kg · ₹${s.amount_paid}` : ""}</p>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
